import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import Loader from "../component/Loader";
import { BASE_URL } from "../api";
import { exportToExcel } from "../utils/exportData";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders } from "../utils/helpers";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";

export default function LiveTrades() {
  const handleError = useHandleError();
  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  const mid = sessionStorage.getItem("mid");

  const [loading, setLoading] = useState(false);
  const [tradeData, setTradeData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    accountType: "demo",
    account: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const clearFilters = () => {
    setFilters({
      accountType: "demo",
      account: "",
    });
    setSearchTerm("");
    setSearchQuery("");
    setTradeData([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (name === "accountType") {
      setFilters((prev) => ({ ...prev, accountType: value, account: "" }));
      setAccounts([]);
      const list = await getAllAccounts(value);
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters((prev) => ({ ...prev, accountType: value, account: firstAcc }));
      if (firstAcc) {
        fetchTradeData({
          accountType: value,
          account: firstAcc,
        });
      }
    }
  };

  const getAllAccounts = async (type) => {
    if (!type) return [];

    const url =
      type === "live"
        ? `${BASE_URL}/MT5M/real_accounts_number_list`
        : `${BASE_URL}/MT5M/demo_account_number_list`;

    try {
      setLoading(true);
      const res = await axios.get(url, {
        headers: getApiHeaders(),
      });

      if (res.data.code === 200) {
        const list = res.data.data || [];
        setAccounts(list);
        return list;
      } else {
        MessageToster("error", res.data.message || "Failed to fetch accounts");
        return [];
      }
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const formatTrade = (trade) => {
    const action = trade.Action !== undefined
      ? (Number(trade.Action) === 0 ? "Buy" : Number(trade.Action) === 1 ? "Sell" : String(trade.Action))
      : (trade.Type ?? "-");

    let openTime = trade.TimeCreate || trade.Open_Time || trade.TimeUpdate || "-";
    if (typeof openTime === "number") {
      openTime = new Date(openTime * 1000).toLocaleString();
    }

    return {
      Account: trade.Account ?? trade.Login ?? "-",
      Ticket: trade.Ticket ?? trade.Position ?? trade.ExpertPositionID ?? "-",
      Symbol: trade.Symbol ?? "-",
      Volume: trade.Volume ?? "-",
      Type: action,
      "Open Time": openTime,
      "Open price": trade.PriceOpen ?? trade.Open_price ?? trade.Open_Price ?? "-",
      "Current Price": trade.PriceCurrent ?? trade.current_price ?? trade.Current_Price ?? "-",
      "T/P": trade.PriceTP ?? trade["T/P"] ?? "-",
      "S/L": trade.PriceSL ?? trade["S/L"] ?? "-",
      Swap: trade.Storage ?? trade.Swap ?? "-",
      "Profit/Loss": trade.Profit ?? "-",
    };
  };

  const fetchTradeData = async (filters) => {
    if (!filters.accountType) {
      return MessageToster("error", "Please select account type");
    }
    if (!filters.account) {
      return MessageToster("error", "Please select account number");
    }
    setLoading(true);
    const filteredAccount = Number(filters.account);
    setSearchQuery(searchTerm);
    try {
      const res = await axios.post(
        `${BASE_URL}/MT5M/my_trade_data?loginid=${filteredAccount}&trade_type=open`,
        {},
        { headers: getApiHeaders() }
      );
      if (res.data.code === 200 && Array.isArray(res.data.data)) {
        // Filter only valid trade objects, ignore MT5 error codes like ["-106"] or ["-1"]
        const validTrades = res.data.data.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            (item.Position !== undefined ||
              item.Ticket !== undefined ||
              item.Symbol !== undefined ||
              item.Login !== undefined ||
              item.Account !== undefined)
        );
        setTradeData(validTrades);
      } else {
        setTradeData([]);
      }
    } catch (error) {
      setTradeData([]);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initPage = async () => {
      const list = await getAllAccounts("demo");
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters((prev) => ({
        ...prev,
        accountType: "demo",
        account: firstAcc,
      }));
      if (firstAcc) {
        fetchTradeData({
          accountType: "demo",
          account: firstAcc,
        });
      }
    };
    initPage();
  }, []);

  const formattedTradeData = (Array.isArray(tradeData) ? tradeData : []).map(formatTrade);
  const filteredData = formattedTradeData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.Account || "").toLowerCase().includes(query) ||
      String(item.Ticket || "").toLowerCase().includes(query) ||
      String(item.Symbol || "").toLowerCase().includes(query) ||
      String(item.Type || "").toLowerCase().includes(query) ||
      String(item["Open Time"] || "").toLowerCase().includes(query) ||
      String(item["Open price"] || "").toLowerCase().includes(query) ||
      String(item["Current Price"] || "").toLowerCase().includes(query) ||
      String(item["T/P"] || "").toLowerCase().includes(query) ||
      String(item["S/L"] || "").toLowerCase().includes(query) ||
      String(item.Swap || "").toLowerCase().includes(query) ||
      String(item["Profit/Loss"] || "").toLowerCase().includes(query)
    );
  });

  return (
    <>
      <div id="layout-wrapper">
        {loading && <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* start page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">Live Trades</h2>
                  </div>
                </div>
              </div>
              {/* end page title */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            {/* <option value="">Account type</option> */}
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            <option value="demo">Demo</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account Number</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account No" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                            {/* {accounts.map((acc) => (
                                                            <option key={acc} value={acc}>
                                                                {acc}
                                                            </option>
                                                        ))} */}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light w-100"
                            onClick={() => fetchTradeData(filters)}
                          >
                            Search
                          </button>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={clearFilters}
                            disabled={!filters.account && !searchTerm}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="ms-auto">
                          <ExportButton
                            disabled={!tradeData || tradeData.length === 0}
                            onClick={() => {
                              const exportData = (Array.isArray(tradeData) ? tradeData : []).map(formatTrade);
                              exportToExcel(
                                exportData,
                                "Live_Trade",
                                "live_trade"
                              );
                            }}
                          />
                        </div>
                      </div>

                      <MobileFilter
                        title="History"
                        onExport={() => {
                          exportToExcel(
                            filteredData,
                            "Live_Trade",
                            "live_trade"
                          );
                        }}
                        exportDisabled={!filteredData || filteredData.length === 0}
                      >
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            <option value="demo">Demo</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account Number</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account No" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light flex-grow-1"
                            onClick={() => fetchTradeData(filters)}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-light waves-effect waves-light border flex-grow-1"
                            onClick={clearFilters}
                            disabled={!filters.account && !searchTerm}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Clear
                          </button>
                        </div>
                      </MobileFilter>

                      <CommonTable
                        columns={[
                          { header: "Account", accessor: "Account" },
                          { header: "Ticket", accessor: "Ticket" },
                          { header: "Symbol", accessor: "Symbol" },
                          { header: "Volume", accessor: "Volume" },
                          { header: "Type", accessor: "Type" },
                          { header: "Open Time", accessor: "Open Time" },
                          { header: "Open price", accessor: "Open price" },
                          { header: "Current Price", accessor: "Current Price" },
                          { header: "T/P", accessor: "T/P" },
                          { header: "S/L", accessor: "S/L" },
                          { header: "Swap", accessor: "Swap" },
                          { header: "Profit/Loss", accessor: "Profit/Loss" },
                        ]}
                        data={filteredData}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>{" "}
                {/* end col */}
              </div>
            </div>
            {/* container-fluid */}
          </div>

          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}
